import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  useGetWorkflowsQuery,
  useDeleteWorkflowMutation,
  useActivateWorkflowMutation,
  useDeactivateWorkflowMutation,
} from '../../../services/WorkflowService';
import { useGetWorkflowTriggersQuery } from '../../../services/WorkflowService';
import { WorkflowListHeader, WorkflowListFilters, WorkflowListContent, ConfirmDeleteDialog } from '../components';
import { PageContainer } from '../../common/components';

const WorkflowListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [triggerFilter, setTriggerFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch dimension data
  const { data: workflowTriggers = [], isLoading: loadingTriggers } = useGetWorkflowTriggersQuery();

  // Build query params based on filters
  const queryParams = {
    triggerType: triggerFilter !== 'all' ? triggerFilter : undefined,
    isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
  };

  const { data: workflows = [], isLoading, isError } = useGetWorkflowsQuery(queryParams);
  const [deleteWorkflow] = useDeleteWorkflowMutation();
  const [activateWorkflow] = useActivateWorkflowMutation();
  const [deactivateWorkflow] = useDeactivateWorkflowMutation();

  // Filter workflows by search term
  const filteredWorkflows = workflows.filter((workflow) =>
    workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workflow.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async () => {
    if (workflowToDelete) {
      try {
        await deleteWorkflow({ id: workflowToDelete }).unwrap();
        setDeleteDialogOpen(false);
        setWorkflowToDelete(null);
      } catch {
        setErrorMessage('Failed to delete workflow. Please try again.');
      }
    }
  };

  const handleActivateToggle = async (id: string, isActive: boolean) => {
    try {
      if (isActive) {
        await deactivateWorkflow({ id }).unwrap();
      } else {
        await activateWorkflow({ id }).unwrap();
      }
    } catch {
      setErrorMessage(`Failed to ${isActive ? 'deactivate' : 'activate'} workflow. Please try again.`);
    }
  };

  const openDeleteDialog = (id: string) => {
    setWorkflowToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleViewWorkflow = (id: string) => {
    navigate(`/workflows/${id}`);
  };

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <Card
          sx={{
            maxWidth: 1400,
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          {/* Header Section */}
          <WorkflowListHeader onCreateClick={() => navigate('/workflows/create')} />

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}

            {/* Filters Section */}
            <WorkflowListFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              triggerFilter={triggerFilter}
              onTriggerFilterChange={setTriggerFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              workflowTriggers={workflowTriggers}
              loadingTriggers={loadingTriggers}
            />

            {/* Content Section */}
            <WorkflowListContent
              workflows={filteredWorkflows}
              isLoading={isLoading}
              isError={isError}
              onView={handleViewWorkflow}
              onActivateToggle={handleActivateToggle}
              onDelete={openDeleteDialog}
            />
          </CardContent>
        </Card>
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default WorkflowListPage;
